import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import { CheckCircle2, Clock, XCircle, Share2, RefreshCw, X } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Btn, Chip, rs } from "./ui";

const ICON = { SUCCESS: [CheckCircle2, "text-[#F26B1D]"], FAILED: [XCircle, "text-red-600"], REFUNDED: [XCircle, "text-sky-600"], PENDING: [Clock, "text-amber-500"] };

export function slipRows(t) {
  const d = t.details || {};
  if (t.service === "Payout") {
    return [["Txn ID", t.txn_id], ["Date", fmtDate(t.created_at)], ["Beneficiary", d.holder_name], ["A/c No", t.number], ["Bank", t.operator],
      ["IFSC", d.ifsc], ["Mode", d.mode], ["UTR", d.utr || (t.status === "PENDING" ? "Awaiting bank" : "-")], ["Charges", rs(t.surcharge)]];
  }
  const rows = [["Txn ID", t.txn_id], ["Date", fmtDate(t.created_at)], ["Service", t.service], ["Number / A/c", t.number], ["Operator / Bank", t.operator]];
  ["customer_name", "beneficiary", "holder_name", "ifsc", "utr", "rrn", "bank_rrn"].forEach((k) => d[k] && rows.push([k.replace(/_/g, " "), String(d[k])]));
  if (t.operator_ref) rows.push(["Operator Ref", t.operator_ref]);
  if (t.surcharge) rows.push(["Charges", rs(t.surcharge)]);
  return rows;
}

export function Slip({ txn, onClose, onUpdate }) {
  const { user, refreshBalance } = useAuth();
  const ref = useRef(null);
  const [t, setT] = useState(txn);
  const [busy, setBusy] = useState(false);
  const polls = useRef(0);
  useEffect(() => { setT(txn); polls.current = 0; }, [txn]);
  const check = async (silent) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/payout/${t.txn_id}/status`);
      setT(data); onUpdate?.(data); refreshBalance();
      if (!silent) toast.info(`Status: ${data.status}`);
    } catch (e) { if (!silent) toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  useEffect(() => {
    if (!t || t.service !== "Payout" || t.status !== "PENDING") return undefined;
    const id = setInterval(() => { if (polls.current++ < 6) check(true); }, 15000);
    return () => clearInterval(id);
  }, [t?.txn_id, t?.status]); // eslint-disable-line
  if (!t) return null;
  const [Icon, tone] = ICON[t.status] || ICON.PENDING;
  const brand = user?.branding?.company_name || user?.shop_name || user?.name;
  const share = async () => {
    try {
      const url = await toPng(ref.current, { pixelRatio: 2, backgroundColor: "#ffffff" });
      const blob = await (await fetch(url)).blob();
      const file = new File([blob], `slip-${t.txn_id}.png`, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) await navigator.share({ files: [file], title: "Transaction slip" });
      else {
        const a = document.createElement("a"); a.href = url; a.download = file.name; a.click();
        window.open(`https://wa.me/?text=${encodeURIComponent(`${brand}\n${t.service} ${rs(t.amount)} ${t.status}\nTxn ID: ${t.txn_id}${t.details?.utr ? `\nUTR: ${t.details.utr}` : ""}`)}`, "_blank");
      }
    } catch (e) { if (e?.name !== "AbortError") toast.error("Could not share slip"); }
  };
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-[#F6F5F1]" data-testid="m-slip">
      <div className="flex h-14 items-center justify-end px-3"><button onClick={onClose} className="grid h-10 w-10 place-items-center rounded-full active:bg-black/5" data-testid="m-slip-close"><X className="h-6 w-6" /></button></div>
      <div className="flex-1 overflow-y-auto px-4 pb-6">
        <div className="mb-4 flex flex-col items-center text-center">
          <Icon className={`h-16 w-16 ${tone}`} strokeWidth={1.6} />
          <div className="mt-2 font-display text-xl font-bold" data-testid="m-slip-status-text">{t.status === "SUCCESS" ? "Successful" : t.status === "PENDING" ? "Processing with bank" : t.status === "REFUNDED" ? "Failed — refunded" : "Failed"}</div>
          {t.status === "FAILED" && t.details?.reason && <div className="mt-1 text-sm text-red-600">{t.details.reason}</div>}
        </div>
        <div ref={ref} className="rounded-3xl bg-white p-5 font-mono text-[13px]" data-testid="m-slip-card">
          <div className="text-center font-display text-base font-extrabold" data-testid="m-slip-brand">{brand}</div>
          <div className="mt-3 text-center font-display text-3xl font-bold" data-testid="m-slip-amount">{rs(t.amount)}</div>
          <div className="mt-1 flex justify-center"><Chip s={t.status} testid="m-slip-status" /></div>
          <div className="mt-4 space-y-1.5 border-t border-dashed border-slate-300 pt-3">
            {slipRows(t).map(([k, v]) => <div key={k} className="flex justify-between gap-3"><span className="capitalize text-slate-500">{k}</span><span className="text-right font-semibold" data-testid={`m-slip-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{v || "-"}</span></div>)}
          </div>
          <div className="mt-4 text-center text-[11px] text-slate-400">Thank you</div>
        </div>
      </div>
      <div className="space-y-2 border-t border-black/5 bg-white p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        {t.service === "Payout" && t.status === "PENDING" && <Btn variant="ghost" onClick={() => check(false)} loading={busy} data-testid="m-slip-check-status"><RefreshCw className="h-5 w-5" />Check Status</Btn>}
        <Btn onClick={share} data-testid="m-slip-share"><Share2 className="h-5 w-5" />Share slip</Btn>
      </div>
    </div>
  );
}
